// Tenant-level AI feature availability (director-controlled). The director
// toggles which AI surfaces the team can use; the SPA hides the disabled ones
// and the backend also enforces them (403). Flags come from GET /me.
import { useQuery } from '@tanstack/react-query'

import { apiRequest } from './client'
import type { AiFeatures, MeResponse, WorkHoursConfig } from './types'

export const AI_FEATURES_ALL_ON: AiFeatures = {
  insights: true,
  ask_ai: true,
  draft_reply: true,
  reactivation: true,
  ai_tab: true,
  ai_digest: true,
  call_analysis: true,
}

/** AI features from the cached /me. Defaults all-on while loading or on error,
 *  so a slow/failed /me never transiently hides a feature (fail-safe). */
export function useAiFeatures(): AiFeatures {
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiRequest<MeResponse>('/api/miniapp/me'),
    staleTime: 60_000,
  })
  return data?.features ?? AI_FEATURES_ALL_ON
}

/** Tenant working hours from the cached /me — so EVERY user's work-day header
 *  reflects what the director saved (not a hardcoded default). Undefined while
 *  loading or against an older backend; callers fall back to local defaults. */
export function useWorkHours(): WorkHoursConfig | undefined {
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiRequest<MeResponse>('/api/miniapp/me'),
    staleTime: 60_000,
  })
  return data?.work_hours
}

/** Director settings: read the tenant's current AI feature flags. */
export function fetchAiFeatures(): Promise<AiFeatures> {
  return apiRequest<AiFeatures>('/api/miniapp/director/ai-features')
}

/** Director settings: flip one or more flags; returns the full resolved set. */
export function updateAiFeatures(patch: Partial<AiFeatures>): Promise<AiFeatures> {
  return apiRequest<AiFeatures>('/api/miniapp/director/ai-features', {
    method: 'PATCH',
    body: patch,
  })
}
